import React, { useState, useRef, useEffect } from 'react';
import { Lot } from '../types/todo';
import { v4 as uuidv4 } from 'uuid';

interface LotDropdownProps {
  lots: Lot[];
  onChange: (lots: Lot[]) => void;
  disabled?: boolean;
}

export const LotDropdown: React.FC<LotDropdownProps> = ({ lots, onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingLotId, setEditingLotId] = useState<string | null>(null);
  const [editingLotNumber, setEditingLotNumber] = useState('');
  const [editingQuantity, setEditingQuantity] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const totalQuantity = lots.reduce((sum, lot) => sum + lot.quantity, 0);

  const handleAddLot = () => {
    const newLot: Lot = {
      id: uuidv4(),
      lotNumber: '',
      quantity: 0
    };
    onChange([...lots, newLot]);
    setEditingLotId(newLot.id);
    setEditingLotNumber('');
    setEditingQuantity(0);
  };

  const handleStartEditLot = (lot: Lot) => {
    setEditingLotId(lot.id);
    setEditingLotNumber(lot.lotNumber);
    setEditingQuantity(lot.quantity);
  };

  const handleSaveLot = (lotId: string) => {
    const updatedLots = lots.map(lot =>
      lot.id === lotId ? { ...lot, lotNumber: editingLotNumber, quantity: editingQuantity } : lot
    );
    onChange(updatedLots);
    setEditingLotId(null);
  };

  const handleCancelEdit = () => {
    // Remove lot if it's a new one that hasn't been saved yet
    const lot = lots.find(l => l.id === editingLotId);
    if (lot && !lot.lotNumber && lot.quantity === 0) {
      onChange(lots.filter(l => l.id !== editingLotId));
    }
    setEditingLotId(null);
  };

  const handleDeleteLot = (lotId: string) => {
    const lot = lots.find(l => l.id === lotId);
    if (lot && window.confirm(`Are you sure you want to delete lot ${lot.lotNumber}?`)) {
      onChange(lots.filter(l => l.id !== lotId));
      if (editingLotId === lotId) {
        setEditingLotId(null);
      }
    }
  };

  const getSummaryText = () => {
    if (lots.length === 0) {
      return 'No lots added';
    }
    return lots.map(lot => lot.lotNumber || '(unnamed)').join(', ');
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '8px',
          fontSize: '15px',
          border: '2px solid #ccc',
          borderRadius: '4px',
          fontWeight: '500',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: disabled ? '#f5f5f5' : 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '38px'
        }}
      >
        <span style={{ flex: 1, color: lots.length === 0 ? '#999' : '#333' }}>{getSummaryText()}</span>
        <span
          style={{
            fontSize: '12px',
            color: '#666',
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            marginLeft: '8px'
          }}
        >
          ▼
        </span>
      </div>

      {isOpen && !disabled && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '2px solid #4CAF50',
            borderRadius: '4px',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto'
          }}
        >
          <div style={{ padding: '8px' }}>
            {lots.length > 0 ? (
              <div style={{ marginBottom: '8px' }}>
                {lots.map((lot, index) => (
                  <div
                    key={lot.id}
                    style={{
                      padding: '8px',
                      backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white',
                      borderRadius: '4px',
                      marginBottom: '4px'
                    }}
                  >
                    {editingLotId === lot.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <input
                          type="text"
                          value={editingLotNumber}
                          onChange={e => setEditingLotNumber(e.target.value)}
                          placeholder="Lot #"
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '6px',
                            fontSize: '14px',
                            border: '1px solid #4CAF50',
                            borderRadius: '4px'
                          }}
                        />
                        <input
                          type="number"
                          value={editingQuantity}
                          onChange={e => setEditingQuantity(Number(e.target.value))}
                          placeholder="Quantity"
                          min="0"
                          style={{
                            width: '100%',
                            padding: '6px',
                            fontSize: '14px',
                            border: '1px solid #4CAF50',
                            borderRadius: '4px'
                          }}
                        />
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            onClick={() => handleSaveLot(lot.id)}
                            style={{
                              flex: 1,
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            style={{
                              flex: 1,
                              backgroundColor: '#757575',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                            {lot.lotNumber || '(unnamed)'}
                          </div>
                          <div style={{ fontSize: '13px', color: '#666' }}>Quantity: {lot.quantity}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            onClick={() => handleStartEditLot(lot)}
                            style={{
                              backgroundColor: '#2196F3',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteLot(lot.id)}
                            style={{
                              backgroundColor: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '13px'
                }}
              >
                No lots added yet
              </div>
            )}

            <div
              style={{
                borderTop: '1px solid #e0e0e0',
                paddingTop: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>Total Quantity: {totalQuantity}</div>
              <button
                onClick={handleAddLot}
                style={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                + Add Lot
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
